import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { Users, CheckSquare, FolderOpen, Activity, Shield } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        axiosInstance.get("/admin/stats"),
        axiosInstance.get("/admin/users"),
      ]);
      setStats(statsResponse.data);
      setUsers(usersResponse.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="admin-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const systemCards = [
    {
      title: "Total Users",
      value: stats?.total_users || 0,
      icon: Users,
      color: "bg-purple-50 text-purple-700",
      testId: "admin-stat-users"
    },
    {
      title: "Total Tasks",
      value: stats?.total_tasks || 0,
      icon: CheckSquare,
      color: "bg-blue-50 text-blue-700",
      testId: "admin-stat-tasks"
    },
    {
      title: "Total Categories",
      value: stats?.total_categories || 0,
      icon: FolderOpen,
      color: "bg-green-50 text-green-700",
      testId: "admin-stat-categories"
    },
    {
      title: "Active Sessions",
      value: stats?.active_sessions || 0,
      icon: Activity,
      color: "bg-orange-50 text-orange-700",
      testId: "admin-stat-sessions"
    },
  ];

  return (
    <div className="p-8" data-testid="admin-panel-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900" data-testid="admin-panel-title">
              Admin Panel
            </h1>
          </div>
          <p className="text-gray-600" data-testid="admin-panel-subtitle">
            System overview and user management
          </p>
        </div>

        {/* System Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {systemCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.title}
                data-testid={stat.testId}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className={`inline-flex p-3 rounded-lg ${stat.color} mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">{stat.title}</h3>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
            );
          })}
        </div>

        {/* Task Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* By Status */}
          <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="tasks-by-status">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Tasks by Status</h2>
            <div className="space-y-3">
              {stats?.tasks_by_status && Object.entries(stats.tasks_by_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-gray-700">{status}</span>
                  <span className="font-semibold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By Priority */}
          <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="tasks-by-priority">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Tasks by Priority</h2>
            <div className="space-y-3">
              {stats?.tasks_by_priority && Object.entries(stats.tasks_by_priority).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <span className="text-gray-700">{priority}</span>
                  <span className="font-semibold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="users-list">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">All Users</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">User</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Role</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Tasks</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Categories</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    data-testid={`user-row-${user.id}`}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.picture} alt={user.name} />
                          <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-gray-900">{user.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                    <td className="py-3 px-4">
                      {user.is_admin ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          <Shield className="w-3 h-3" />
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          User
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-900">{user.task_count}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-900">{user.category_count}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
